import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { CONFIG_OVERRIDE_LABELS } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { ON_OFF } from '#schemas/config-override/helpers/enums.ts';
import { mapFormToOverrides } from '#screens/hosts/form/advanced/mappers/mapFormToOverrides.ts';
import { schemas, type GraphiteFormData } from '../config-forms/form-data.ts';

const DEFAULT_VALUES = {
  stats: {
    type: 'graphite' as const,
    host: '',
    prefix: '',
    interval: null,
    lighthouse_metrics: '' as const,
    message_metrics: '' as const,
  },
};
type FormData = GraphiteFormData;

type Props = {
  defaultValues?: FormData;
  onSave: (overrides: ConfigOverride[]) => void;
};

export function GraphiteForm({ defaultValues, onSave }: Props) {
  const { register, formState, handleSubmit, control } = useForm({
    resolver: zodResolver(schemas.graphite),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      return Promise.resolve(onSave(mapFormToOverrides(data)));
    })(e);
  };

  const errors = formState.errors;

  return (
    <form method="post" id="graphite-form" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide">
        {/* hidden field to include stats.type in the overrides that are saved */}
        <Input hidden {...register('stats.type')} />
        <Field label={CONFIG_OVERRIDE_LABELS['stats.host']}>
          <Input {...register('stats.host')} className="width-100" hasError={Boolean(errors.stats?.host)} />
          {errors.stats?.host && <ErrorMessage>{errors.stats.host.message?.toString()}</ErrorMessage>}
        </Field>
        <Field label={CONFIG_OVERRIDE_LABELS['stats.prefix']}>
          <Input {...register('stats.prefix')} className="width-100" hasError={Boolean(errors.stats?.prefix)} />
          {errors.stats?.prefix && <ErrorMessage>{errors.stats.prefix.message?.toString()}</ErrorMessage>}
        </Field>

        {/* Shared fields */}
        <Field label={CONFIG_OVERRIDE_LABELS['stats.interval']}>
          <Input
            {...register('stats.interval')}
            className="width-100"
            type="text"
            max={255}
            inputMode="numeric"
            hasError={Boolean(errors.stats?.interval)}
          />
          {errors.stats?.interval && <ErrorMessage>{errors.stats.interval.message?.toString()}</ErrorMessage>}
        </Field>
        <SelectControlled
          label={CONFIG_OVERRIDE_LABELS['stats.lighthouse_metrics']}
          name="stats.lighthouse_metrics"
          control={control}
          hasError={Boolean(errors.stats?.lighthouse_metrics)}
          renderValue={(item) => item || null}
          renderOptions={({ onChange }) => (
            <>
              {ON_OFF.map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
        <SelectControlled
          label={CONFIG_OVERRIDE_LABELS['stats.message_metrics']}
          name="stats.message_metrics"
          control={control}
          hasError={Boolean(errors.stats?.message_metrics)}
          renderValue={(item) => item || null}
          renderOptions={({ onChange }) => (
            <>
              {ON_OFF.map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
      </Spacer>
    </form>
  );
}
